/*
 * @providesModule PhoneInput
 *
 */
import React, {Component} from 'react';

import _ from 'lodash';
import Country from 'Country';
import CountryPicker from 'CountryPicker';
import Flags from 'FlagResource';
import googleLibphonenumber from 'google-libphonenumber';
import {_m} from 'INTL';
import Optional from 'Optional';
import PhoneNumber from 'PhoneNumber';
import PropTypes from 'prop-types';
import SkillzImage from 'SkillzImage';
import SkillzSVG from 'SkillzSVG';
import styles from 'PhoneInputStyle';
import {TextField} from 'react-native-material-textfield';

import {
  Platform,
  TextInput,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from 'react-native';
import {Picker} from '@react-native-picker/picker';

const AsYouTypeFormatter = googleLibphonenumber.AsYouTypeFormatter;

const TEST_IDS = {
  COUNTRY_SELECTOR: 'personal_info_country_selector',
};

export default class PhoneInput extends Component {
  static propTypes = {
    textComponent: PropTypes.func,
    initialCountry: PropTypes.string,
    onChangePhoneNumber: PropTypes.func,
    onPressFlag: PropTypes.func,
    value: PropTypes.string,
    style: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
    flagStyle: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
    textStyle: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
    offset: PropTypes.number,
    textProps: PropTypes.object,
    onSelectCountry: PropTypes.func,
    pickerButtonColor: PropTypes.string,
    pickerBackgroundColor: PropTypes.string,
    pickerItemStyle: PropTypes.object,
    cancelText: PropTypes.string,
    cancelTextStyle: PropTypes.object,
    confirmText: PropTypes.string,
    confirmTextTextStyle: PropTypes.object,
    valuePhoneNumber: PropTypes.string,
    label: PropTypes.string,
    isFlagRightSide: PropTypes.bool,
    // Determines whether or not to show the arrow icon next to the country picker flag to indicate
    // the dropdown.
    showArrow: PropTypes.bool,
    // Determines whether or not to format the phone number with region-appropriate special characters
    // as the user types.
    formatWhileTyping: PropTypes.bool,
    // Callback that is invoked when the phone number becomes valid or invalid. Example use case would
    // be a parent form that has its submit button disabled until all fields become valid.
    // Typescript definition for this function: `setSubmitEnabled?: ((enabled: boolean) => void) | null`
    setSubmitEnabled: PropTypes.func,
    // Custom validation to be applied to the phone number when determining if it is considered 'valid'
    // for the purposes of the setSubmitEnabled prop.
    // Typescript definition for this function: `validation?: ((phoneNumber: string) => boolean) | null`
    validation: PropTypes.func,
    phoneVerified: PropTypes.bool,
  };

  static defaultProps = {
    isFlagRightSide: false,
    showArrow: false,
    formatWhileTyping: false,
  };

  constructor(props: unknown, context: unknown) {
    super(props, context);

    this.onChangePhoneNumber = this.onChangePhoneNumber.bind(this);
    this.onPressFlag = this.onPressFlag.bind(this);
    this.selectCountry = this.selectCountry.bind(this);
    this.getFlag = this.getFlag.bind(this);

    const iso2 = props.initialCountry || 'us';
    const countryData = PhoneNumber.getCountryDataByCode(iso2);

    this._phoneInput = React.createRef();

    const dialCode =
      props.valuePhoneNumber && !props.formatWhileTyping
        ? PhoneNumber.getCountryCodeOfNumber(props.valuePhoneNumber)
        : _.get(countryData, 'dialCode', 1);
    let initialNumber = countryData || props.valuePhoneNumber ? `+${dialCode}` : '';
    if (props.valuePhoneNumber) {
      const numberWithCode = `${initialNumber}${props.valuePhoneNumber}`;
      initialNumber = PhoneNumber.isValidNumber(props.valuePhoneNumber)
        ? props.valuePhoneNumber
        : this._formatNumber(numberWithCode, dialCode);
    }
    if (props.formatWhileTyping) {
      initialNumber = this._formatNumber(initialNumber);
    }

    this.state = {
      iso2,
      countryCodeLength: dialCode.length,
      formattedNumber: initialNumber,
      value: null,
    };
    if (this.props.value) {
      const countryCode = PhoneNumber.getCountryCodeOfNumber(this.props.value);
      this.updateFlagAndFormatNumber(
        this.props.formatWhileTyping ? `+${countryCode}${this.props.value}` : this.props.value
      );
    }
  }

  focus() {
    this._focusTextInputField();
  }
  componentDidMount() {
    const iso2 = this.props.valuePhoneNumber
      ? PhoneNumber.getCountryCodeOfNumber(this.props.valuePhoneNumber)
      : this.state.iso2;
    const countryData = PhoneNumber.getCountryDataByCode(iso2);
    const dialCode = _.get(countryData, 'dialCode', 1);
    const countryCodeLength = dialCode.length;
    if (this.state.countryCodeLength !== countryCodeLength) {
      this.setState({countryCodeLength});
    }
    if (this.state.iso2 != iso2) {
      this.setState({iso2});
    }
  }

  componentDidUpdate(prevProps: Object) {
    if (
      prevProps.value !== this.props.value ||
      prevProps.initialCountry !== this.props.initialCountry
    ) {
      if (this.props.value !== this.state.value && this.props.value) {
        this.setState({value: this.props.value});
        this.updateFlagAndFormatNumber(this.props.value);
      }
      if (
        this.props.initialCountry !== this.state.initialCountry &&
        !this.props.valuePhoneNumber &&
        !this.props.formatWhileTyping
      ) {
        const countryData = PhoneNumber.getCountryDataByCode(this.props.initialCountry);
        const dialCode = _.get(countryData, 'dialCode', 1);
        this.setState({iso2: this.props.initialCountry, countryCodeLength: dialCode.length});
        this.updateFlagAndFormatNumber(dialCode);
      }
    }
  }

  updateFlagAndFormatNumber(number: string, actionAfterSetState: Function = null) {
    let iso2 = this.props.initialCountry || 'us';
    let phoneNumber = number;
    if (number) {
      if (phoneNumber[0] != '+') {
        phoneNumber = `+${phoneNumber}`;
      }
      iso2 = PhoneNumber.getCountryCodeOfNumber(phoneNumber) || this.state.iso2;
    }

    if (number.length <= this.state.countryCodeLength) {
      if (this.props.textComponent === TextField && this._phoneInput.current) {
        this._phoneInput.current?.setValue(this.state.formattedNumber);
      }
      return;
    }

    if (this.props.formatWhileTyping) {
      phoneNumber = this._formatNumber(number, iso2);
    }

    if (this.props.textComponent === TextField && this._phoneInput.current) {
      this._phoneInput.current?.setValue(phoneNumber);
    }

    this._checkSubmitEnabled(phoneNumber, iso2);

    this.setState({iso2, formattedNumber: phoneNumber}, actionAfterSetState);
  }

  _checkSubmitEnabled(phoneNumber: string, iso2: string) {
    if (!this.props.setSubmitEnabled) {
      return;
    }

    let isValid = phoneNumber.length > this.state.countryCodeLength + 1;
    if (this.props.validation) {
      isValid = isValid && this.props.validation(phoneNumber);
    } else {
      isValid = isValid && PhoneNumber.isValidNumber(phoneNumber, iso2);
    }

    this.props.setSubmitEnabled(isValid);
  }

  _formatNumber(number: string, iso2: string): string {
    const formatter = new AsYouTypeFormatter(iso2);
    const regex = /[^+\d]/g;
    let phoneNumber = number.replace(regex, '');
    phoneNumber = phoneNumber.split(' ').join('');
    [...phoneNumber].forEach((digit: string) => {
      formatter.inputDigit(digit);
    });
    phoneNumber = formatter.currentOutput_;
    formatter.clear();

    if (phoneNumber.length <= 1 || phoneNumber.match(/[+]/g).length > 1) {
      phoneNumber = `+${_.get(iso2, 'dialCode', 1)}`;
    }
    return phoneNumber;
  }

  onChangePhoneNumber(number: string) {
    const iso2 = this.props.initialCountry || 'us';
    const actionAfterSetState = this.props.onChangePhoneNumber
      ? () => {
          this.props.onChangePhoneNumber(number);
        }
      : null;
    this.updateFlagAndFormatNumber(number, actionAfterSetState);
    this._checkSubmitEnabled(number, iso2);
  }

  isValidNumber(): boolean {
    return PhoneNumber.isValidNumber(this.state.formattedNumber, this.state.iso2);
  }

  getNumberType() {
    return PhoneNumber.getNumberType(this.state.formattedNumber, this.state.iso2);
  }

  getValue(): string {
    return this.state.formattedNumber;
  }

  getFlag(iso2: unknown): string {
    return Flags.get(iso2);
  }

  getAllCountries() {
    return PhoneNumber.getAllCountries();
  }

  getPickerData() {
    return PhoneNumber.getAllCountries().map((country: string, index: number): Object => {
      return {
        key: index,
        image: Flags.get(country.iso2),
        label: country.name,
        dialCode: `+${country.dialCode}`,
        iso2: country.iso2,
      };
    });
  }

  static getCountryCode(country: string): string {
    const dialCode = _.get(country, 'dialCode', 1);
    return `+${dialCode}`;
  }

  selectCountry(iso2: unknown) {
    if (this.state.iso2 != iso2) {
      const countryData = PhoneNumber.getCountryDataByCode(iso2);
      const dialCode = _.get(countryData, 'dialCode', 1);
      if (countryData) {
        this.setState(
          {
            iso2,
            countryCodeLength: dialCode.length,
            formattedNumber: `+${dialCode}`,
          },
          () => {
            this.props.setSubmitEnabled?.(false);

            this.props.onSelectCountry?.(iso2);

            if (this.props.textComponent === TextField) {
              this._phoneInput.current?.setValue(this.state.formattedNumber);
            }
          }
        );
      }
    }
    this._focusTextInputField();
  }

  onPressFlag() {
    if (this.props.onPressFlag) {
      this.props.onPressFlag();
    } else {
      if (this.state.iso2) {
        this._picker.selectCountry(this.state.iso2);
      }
      this._picker.show();
    }
  }

  _focusTextInputField() {
    setTimeout(() => {
      this._phoneInput.current?.focus();
    }, 0);
  }

  _onPressed(): boolean {
    return this.onPressFlag();
  }

  _onFieldPressed(): boolean {
    return this._onPressed();
  }

  _onFieldFocused = () => {
    if (Platform.OS == 'android') {
      this.setState({textFocused: true});
    }
  };

  _onFieldBlurred = () => {
    if (Platform.OS == 'android') {
      this.setState({textFocused: false});
    }
  };

  _getAllCountries(): Array<Object> {
    const map = Country.getAll().map((country: string, index: number): React.ReactNode => {
      return CountryPicker.renderItemAndroid(country, index);
    }, this);

    const nonNullMap = map.filter((obj: Object): boolean => {
      return !!obj;
    });
    return nonNullMap && nonNullMap.length > 0 ? nonNullMap : null;
  }

  _renderPickerAndroid(): React.ReactNode {
    return (
      <Picker
        style={styles.androidPicker}
        ref={(ref: unknown) => {
          this._picker = ref;
        }}
        selectedValue={this.state.iso2}
        onValueChange={this.selectCountry}
        mode={'dialog'}
        testID={'country_picker'}
      >
        {this._getAllCountries()}
      </Picker>
    );
  }

  _renderPickeriOS(): React.ReactNode {
    return (
      <CountryPicker
        ref={(ref: unknown) => {
          this._picker = ref;
        }}
        selectedCountry={this.state.iso2}
        onSubmit={this.selectCountry}
        onCancel={this.cancelPicker.bind(this)}
      />
    );
  }

  get textFocused(): boolean {
    return this._phoneInput.current?.isFocused() ?? false;
  }

  _renderCaptureView(): React.ReactNode {
    return (
      <View
        style={styles.fillView}
        onStartShouldSetResponder={(): boolean => {
          return !this.textFocused;
        }}
        onResponderGrant={this._onPressed.bind(this)}
        pointerEvents={this.textFocused ? 'none' : 'auto'}
      />
    );
  }

  cancelPicker() {
    this._focusTextInputField();
  }

  _renderTextField(): React.ReactNode {
    const TextComponent = this.props.textComponent || TextInput;

    return (
      <View style={this.props.style}>
        <TextComponent
          ref={this._phoneInput}
          autoCorrect={false}
          autoCompleteType={'off'}
          importantForAutofill={'no'}
          style={this.props.textStyle}
          onChangeText={(text: string) => {
            this.onChangePhoneNumber(text);
          }}
          keyboardType={'phone-pad'}
          underlineColorAndroid='rgba(0,0,0,0)'
          value={this.getValue()}
          onFocus={this._onFieldFocused}
          onBlur={this._onFieldBlurred}
          accessible={true}
          accessibilityLabel={this.props.label}
          testID={'phone_number'}
          label={this.props.label}
          enablesReturnKeyAutomatically={Platform.OS == 'ios'}
          {...this.props.textProps}
          disabled={this.props.phoneVerified}
        />
      </View>
    );
  }

  _renderFlagIcon(): React.ReactNode {
    return (
      <React.Fragment>
        <SkillzImage
          source={Flags.get(this.state.iso2)}
          resizeMode='stretch'
          style={[styles.flag, this.props.flagStyle]}
        />
        <Optional test={this.props.showArrow}>
          <SkillzSVG
            source={'icon_down_arrow.svg'}
            style={styles.flagArrowIcon}
            tintColor={'#999999'}
          />
        </Optional>
      </React.Fragment>
    );
  }

  _getWrapperStyle(): ViewStyle {
    const marginLeft = this.props.showArrow ? 25 : 10; // eslint-disable-line no-magic-numbers
    const isWeb = Platform.OS === 'web';

    return [
      styles.container,
      this.props.style,
      this.props.isFlagRightSide
        ? {flexDirection: 'row-reverse', marginLeft: isWeb ? 0 : marginLeft}
        : {},
    ];
  }
  _renderAndroidCustomFlag(): React.ReactNode {
    return (
      <View style={styles.flagContainer} testID={TEST_IDS.COUNTRY_SELECTOR}>
        <TouchableOpacity
          style={styles.flagButtonContainer}
          onPress={() => {
            this.props.onPressFlag();
          }}
        >
          <SkillzImage
            source={Flags.get(this.state.iso2)}
            resizeMode='stretch'
            style={[styles.flag, this.props.flagStyle]}
          />
        </TouchableOpacity>
      </View>
    );
  }
  _renderAndroidFlag(): React.ReactNode {
    return (
      <View style={styles.flagButtonContainer} testID={TEST_IDS.COUNTRY_SELECTOR}>
        {this._renderFlagIcon()}
        {this._renderPickerAndroid()}
      </View>
    );
  }

  _renderAndroid(): React.ReactNode {
    return (
      <View
        style={this._getWrapperStyle()}
        accessible={true}
        accessibilityRole={'button'}
        testID={'phone_number_country'}
        accessibilityLabel={_m('Country: {0}', this.state.iso2)}
      >
        {!this.props.onPressFlag ? this._renderAndroidFlag() : this._renderAndroidCustomFlag()}
        {this._renderTextField()}
      </View>
    );
  }

  _renderIOS(): React.ReactElement {
    return (
      <View style={this._getWrapperStyle()}>
        <TouchableWithoutFeedback
          onPress={this._onPressed.bind(this)}
          style={styles.flagContainer}
          accessible={true}
          accessibilityRole={'button'}
          testID={'phone_number_country'}
          accessibilityLabel={_m('Country: {0}', this.state.iso2)}
        >
          <View style={styles.flagContainer} testID={TEST_IDS.COUNTRY_SELECTOR}>
            {this._renderFlagIcon()}
            {this._renderCaptureView()}
          </View>
        </TouchableWithoutFeedback>
        {this._renderPickeriOS()}
        {this._renderTextField()}
      </View>
    );
  }

  render(): React.ReactNode {
    if (Platform.OS === 'android' || Platform.OS === 'web') {
      return this._renderAndroid();
    }
    return this._renderIOS();
  }
}
